/**
 * The billing kit's label seam for sak (Plan 02 D1): the app's own `billing.*`
 * locale subtree mapped onto `BillingLabelsProvider`'s shape, exactly like the
 * hub's settings content does. English defaults cover any key the bundle ever
 * lacks (per-key fallback inside the provider).
 */
import { useTranslation } from "react-i18next";
import type { BillingLabels } from "@suite/billing";

type SakT = ReturnType<typeof useTranslation>["t"];

export function sakBillingLabels(t: SakT): Partial<BillingLabels> {
  return {
    retry: t("billing.retry"),
    close: t("billing.close"),
    loading: t("billing.loading"),
    loadMore: t("billing.loadMore"),
    planFree: t("billing.planFree"),
    planTier: (tier) => t("billing.planTier", { tier }),
    meterOf: (used, limit) => t("billing.meterOf", { used, limit }),
    meterUnlimited: (used) => t("billing.meterUnlimited", { used }),
    resetsOn: (date) => t("billing.resetsOn", { date }),
    meterDegraded: t("billing.meterDegraded"),
    meterOverLimit: t("billing.meterOverLimit"),
    meterFileCap: t("billing.meterFileCap"),
    usageTitle: t("billing.usageTitle"),
    usageSubtitle: t("billing.usageSubtitle"),
    serviceStorage: t("billing.serviceStorage"),
    serviceBandwidth: t("billing.serviceBandwidth"),
    serviceAi: t("billing.serviceAi"),
    topUpsTitle: t("billing.topUpsTitle"),
    topUpBuy: (price) => t("billing.topUpBuy", { price }),
    paywallTitle: (service) => t("billing.paywallTitle", { service }),
    paywallBody: (service) => t("billing.paywallBody", { service }),
    upgradeCta: t("billing.upgradeCta"),
    checkoutTitle: (plan) => t("billing.checkoutTitle", { plan }),
    couponPlaceholder: t("billing.couponPlaceholder"),
    couponApply: t("billing.couponApply"),
    couponValid: (amount) => t("billing.couponValid", { amount }),
    couponInvalid: t("billing.couponInvalid"),
    payButton: (price) => t("billing.payButton", { price }),
    checkoutPending: t("billing.checkoutPending"),
    checkoutSuccess: t("billing.checkoutSuccess"),
    checkoutFailed: (detail) => t("billing.checkoutFailed", { detail }),
    checkoutClose: t("billing.checkoutClose"),
    paywallBlockedTitle: t("billing.paywallBlockedTitle"),
    trialActive: (date) => t("billing.trialActive", { date }),
    pausedResumes: (date) => t("billing.pausedResumes", { date }),
    graceTitle: t("billing.graceTitle"),
    graceBody: (date) => t("billing.graceBody", { date }),
    pastDueTitle: t("billing.pastDueTitle"),
    pastDueBody: t("billing.pastDueBody"),
    updatePaymentMethod: t("billing.updatePaymentMethod"),
    billingSectionTitle: t("billing.billingSectionTitle"),
    billingSectionEmpty: t("billing.billingSectionEmpty"),
    paymentMethodsTitle: t("billing.paymentMethodsTitle"),
    paymentMethodAdd: t("billing.paymentMethodAdd"),
    paymentMethodDefault: t("billing.paymentMethodDefault"),
    paymentMethodRemove: t("billing.paymentMethodRemove"),
    paymentMethodRemoveConfirm: t("billing.paymentMethodRemoveConfirm"),
    invoicesTitle: t("billing.invoicesTitle"),
    invoicesEmpty: t("billing.invoicesEmpty"),
    invoiceDownload: t("billing.invoiceDownload"),
    creditNotesTitle: t("billing.creditNotesTitle"),
    recoveryTitle: t("billing.recoveryTitle"),
    managePlan: t("billing.managePlan"),
    devSimulateTitle: t("billing.devSimulateTitle"),
    devSimulateSuccess: t("billing.devSimulateSuccess"),
    devSimulateFailure: t("billing.devSimulateFailure"),
    devCardTitle: t("billing.devCardTitle"),
    devCardInsert: t("billing.devCardInsert"),
  };
}
